import { MAX_LINES_TRIGGER_PROMPT } from "cfg/triggers";
import { EditableAutosizedInput, EditableList, EditableListProps } from "components/EditableList";
import { Filter, FilterOption, FilterOption__STRICT, FilterType } from "components/Filter";
import { FilterSelector, getDefaultFilterOption, getDefaultMetadataFilterOption, getDefaultMultiMetadataFilterOption, Option, PopulationFilter, PROSPECT_OPTIONS, ViewType } from "components/FilterSelector";
import { Loader } from "components/Loader/Loader";
import { PromptOnLeave } from "components/PromptOnLeave";
import { PlaysSidebar } from "./PlaysSidebar";
import { sleep } from "core";
import { Action, AnyAction, Dispatch } from "@reduxjs/toolkit";
import { NO_LOOKBACK_RULE, NO_PARTY_ROLE, PartyRole, Remark, UserGroup, REMARK_TO_HUMAN_READABLE, REMARKS_ALLOWED, REMARKS_BEHAVIORS, REMARKS_OBJECTIONS, REMARKS_OUTCOMES } from "interfaces/db";
import { CnfFilterType, LookbackRule, ProspectFilterTerm, TemporalFilterDisjunction, TimeFilterType, UserFilterTerm } from "interfaces/plays";
import { Condition, Play } from "interfaces/plays";
import { ProspectInfoChoices } from "interfaces/metadata";
import { DEFAULT_LANGUAGE_SETTING, LANGUAGE_SETTING_TO_HUMAN_READABLE } from "interfaces/users";
import { VisibleAccountsResult } from "interfaces/users";
import { Typography } from "interfaces/typography";
import React from "react";
import { connect } from "react-redux";
import uuid from "react-uuid";
import { getServicesManager } from "services";
import { ELIGIBLE_FOR_SPANISH } from "cfg/const";
import { RootState } from "store";
import { convertFromReduxSafeVisibleAccounts } from "lib/redux/users/slice";
import { RequiresAdminAccess, PrimaryActionButton } from "utils/CommonUIComponents";

enum SyncStatus {
    PENDING = 'PENDING',
    HAS_ERRORS = 'HAS_ERRORS',
    READY_TO_DEPLOY = 'READY_TO_DEPLOY',
    DEPLOYING = 'DEPLOYING',
    SAVED = 'SAVED'
}

export type RepSuggestion = {
    promptsProps: EditableListProps,
    reference?: React.RefObject<HTMLDivElement>
    focus?: boolean,
    errorFocus?: boolean,
    onErrorFocus?: () => void,
}

const _DEFAULT_DURATION_TIME = 8
const _DEFAULT_ID = '__DEFAULT__'

// Helper function to get short form remark mapping like in FilterSelector
function getShortRemarkMapping(): {[key in Remark]: string} {
    const mapping: {[key in Remark]: string} = {} as any;
    
    const allRemarks = [...Array.from(REMARKS_OBJECTIONS), ...Array.from(REMARKS_BEHAVIORS), ...Array.from(REMARKS_OUTCOMES)]
        .filter((value: [Remark, [string, string]]) => REMARKS_ALLOWED.includes(value[0]));
    
    for (const [remark, [shortName, id]] of allRemarks) {
        mapping[remark] = shortName;
    }
    
    return mapping;
}

const SHORT_REMARK_MAPPING = getShortRemarkMapping();

function constructDefaultCondition(number: number): Condition {
    return {
        'active': true,
        'condition_group': '',
        'condition_id': _DEFAULT_ID,
        'temp_condition_id': uuid(),
        'condition_name': `Condition #${number}`,
        'created_at': new Date(),
        'filter_cnf': [],
        'team_id': '',
        'updated_at': new Date(),
    }
}

function constructDefaultPlay(condition: Condition, number: number, otherPlays: Play[]): Play {
    let minDuration = _DEFAULT_DURATION_TIME
    const otherPlayDurations = otherPlays.find((v) => v.min_duration !== null)
    if (otherPlayDurations && otherPlayDurations.min_duration !== null) minDuration = otherPlayDurations.min_duration
    else {
        const timeFilter = condition.filter_cnf.find((v) => v.filter_type === CnfFilterType.TIME && v.time_filters && v.time_filters.length > 0 && v.time_filters.filter((v) => v.start === undefined && v.end !== undefined).length === v.time_filters.length)
        if (timeFilter && timeFilter.time_filters && timeFilter.time_filters[0].end !== undefined) minDuration = _DEFAULT_DURATION_TIME    
    }

    return {
        'active': true,
        'condition_id': condition.condition_id,
        'temp_condition_id': condition.temp_condition_id,
        'created_at': new Date(),
        'min_duration': minDuration,
        'play_id': _DEFAULT_ID,
        'temp_play_id': uuid(),
        'play_name': `Suggestion #${number}`,
        'prompt_text': '',
        'team_id': '',
        'weight': 1,
        'updated_at': new Date(),
    }
}

function renderRemovalButton(onClick: () => void, showHidden?: boolean): JSX.Element {
    return ( 
    <div className="relative w-full justify-end flex h-0">
    <button onClick={onClick} tabIndex={-1} type="button" 
    style={showHidden === undefined ? {'zIndex': '999999999999999'} : {'zIndex': '999999999999999', 'display': showHidden ? 'flex' : 'none' }}
    className={
    "absolute -top-1.5 -right-2 h-3.5 w-3.5 items-center justify-center rounded-full bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 cursor-pointer transition-colors duration-200" + (showHidden === undefined ? " hidden group-hover:flex" : "")}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-red-600">
            <path d="M6 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
     </button>
    </div>)
}

type PlayProps = {
  play: Play
  onRemoval: () => void
  updatePlayName: (n: string) => void
  numberLeft: number
  EditableListProps: EditableListProps
}

type IndividualPlayState = {
    hovered: boolean
}

class PlayComponent extends React.Component<PlayProps, IndividualPlayState> {
    constructor(props: PlayProps) {
        super(props)
        this.state = {
            hovered: false,
        }
    }

    render() {
        return <div 
        onMouseEnter={() => this.setState({'hovered': true})}
        onMouseLeave={() => this.setState({'hovered': false})} 
        className='h-fit w-full bg-white rounded-lg p-2' 
        style={{'width': '320px'}}>
            {this.props.numberLeft > 1 ? renderRemovalButton(this.props.onRemoval, this.state.hovered) : null}            
            <div className="w-full flex flex-row items-center justify-start gap-2 mb-2">
                <Typography variant="largeParagraph" color="#374151" fontWeight={600}>
                        Name:
                    </Typography>
                    <EditableAutosizedInput 
                        fontSize={12}
                        editFunctions={{
                            'addEntry': () => {},
                            'removeEntry': () => {},
                            'updateEntry': (idx: number, updatedEntry: string) => {
                                this.props.updatePlayName(updatedEntry)
                            }
                        }}
                        placeholder={'Enter play name'}
                        prompt={this.props.play.play_name}
                        numberLeft={1}
                        idx={0}
                    maxWidth={"200px"}
                        minHeight={"15px"}
                    />
            </div>
            <EditableList {...this.props.EditableListProps}/>
        </div>
    }
}

type PlayConditionComponentProps = {
    plays: Play[],
    condition: Condition
    groupIdToGroup: Map<string, UserGroup>
    updateCondition: (updatedCondition: Condition) => void
    updatePlays: (updatedPlays: Play[]) => void
    onForceSave: () => void
    onRemove: () => void

    syncedStatus: SyncStatus
    syncErrors?: string[] 
    totalPlays: number
    totalPossibleMatches?: number
    visibleAccounts: VisibleAccountsResult | null
    prospectOptions: ProspectInfoChoices | null
}

type PlayConditionComponentState = {
    showErrors?: boolean
    showRemove: boolean
}

class PlayConditionComponentImpl extends React.Component<PlayConditionComponentProps, PlayConditionComponentState> {
    
    constructor(props: PlayConditionComponentProps) {
        super(props)
        this.state = {
            showRemove: false,
            showErrors: false,
        }
    }

    _jsonifyParams(params: any) {
        return Array.from(Object.entries({...params, 'deployRequested': undefined})).map(([key, value]) => JSON.stringify(value)).join('')
    }

    hasPlayErrors() {
        return this.props.syncErrors && this.props.syncErrors.some((v) => !v.includes('is missing suggestions'))
    }

    _getDefaultFilterOption(option: Option) {
        return getDefaultFilterOption(option, undefined, undefined, this.props.prospectOptions ?? undefined, undefined)
    }

    _getDefaultMetadataFilterOption(option: Option) {
        return getDefaultMetadataFilterOption(option)
    }

    _getDefaultMetadataPlayFilterOption(
        option: Option, lookback_rule?: LookbackRule, 
        party_role?: PartyRole, lookback_horizon?: number,
        timeFilterType?: TimeFilterType) {
        return getDefaultMultiMetadataFilterOption(option, lookback_rule, party_role, lookback_horizon, timeFilterType)
    }

    _getEditableListProps(play: Play): EditableListProps {
        return {
            labels: play.prompt_text.split('\n'),
            editFunctions: {
              addEntry: () => {
                const correspondingPlayIdx = this.props.plays.findIndex((v) => (v.temp_play_id && v.temp_play_id === play.temp_play_id) || (v.play_id !== _DEFAULT_ID && v.play_id === play.play_id))
                if (correspondingPlayIdx === -1) return 
                const currentLabels = play.prompt_text.split('\n')
                const updatedLabels = [...currentLabels, '']
                const promptText: string = updatedLabels.join('\n')
                const updatedPlays: Play[] = [...this.props.plays.slice(0, correspondingPlayIdx), {...play, 'prompt_text': promptText}, ...this.props.plays.slice(correspondingPlayIdx + 1)]
                this.props.updatePlays(updatedPlays)
              },
              updateEntry: (idx: number, updatedEntry: string, forceRefreshOnEnter?: boolean) => {
                const correspondingPlayIdx = this.props.plays.findIndex((v) => (v.temp_play_id && v.temp_play_id === play.temp_play_id) || (v.play_id !== _DEFAULT_ID && v.play_id === play.play_id))
                if (correspondingPlayIdx === -1) return 
                const currentLabels = play.prompt_text.split('\n')
                if (idx >= currentLabels.length) return
                const updatedLabels = [...currentLabels.slice(0, idx), updatedEntry, ...currentLabels.slice(idx + 1)]
                const promptText: string = updatedLabels.join('\n')
                const updatedPlays: Play[] = [...this.props.plays.slice(0, correspondingPlayIdx), {...play, 'prompt_text': promptText}, ...this.props.plays.slice(correspondingPlayIdx + 1)]
                this.props.updatePlays(updatedPlays)
              },
              removeEntry: (idx: number) => {
                const correspondingPlayIdx = this.props.plays.findIndex((v) => (v.temp_play_id && v.temp_play_id === play.temp_play_id) || (v.play_id !== _DEFAULT_ID && v.play_id === play.play_id))
                if (correspondingPlayIdx === -1) return 
                const currentLabels = play.prompt_text.split('\n')
                if (idx >= currentLabels.length) return
                const updatedLabels = [...currentLabels.slice(0, idx), ...currentLabels.slice(idx + 1)]
                const promptText: string = updatedLabels.join('\n')
                const updatedPlays: Play[] = [...this.props.plays.slice(0, correspondingPlayIdx), {...play, 'prompt_text': promptText}, ...this.props.plays.slice(correspondingPlayIdx + 1)]
                this.props.updatePlays(updatedPlays)
              }
            },
            listNumberLimit: MAX_LINES_TRIGGER_PROMPT,
            onlyInputs: true,
        }
    }

    _convertFilterTypeToPopulationFilter(filterCnf: TemporalFilterDisjunction): PopulationFilter[] {
        const populationFilters: PopulationFilter[] = []
        if (filterCnf.filter_type === CnfFilterType.PROSPECT) {
            // prospect info
            if (!filterCnf.prospect_filters) return populationFilters
            const cadence_steps: string[] = []
            const cadences: string[] = []
            const industries: string[] = []
            const titles: string[] = []
            const seniorities: string[] = []
   
            for (const prospectInfo of filterCnf.prospect_filters) {
                if (prospectInfo.cadence_step) cadence_steps.push(prospectInfo.cadence_step)
                if (prospectInfo.cadence) cadences.push(prospectInfo.cadence)
                if (prospectInfo.industry) industries.push(prospectInfo.industry)
                if (prospectInfo.seniority) seniorities.push(prospectInfo.seniority)
                if (prospectInfo.title) titles.push(prospectInfo.title)
            }

            const hasCadenceStep = filterCnf.prospectFilterPrimarilyFor === 'CADENCE_STEP'
            const hasCadence = filterCnf.prospectFilterPrimarilyFor === 'CADENCE'
            const hasIndustry = filterCnf.prospectFilterPrimarilyFor === 'INDUSTRY'
            const hasTitles = filterCnf.prospectFilterPrimarilyFor === 'TITLE'
            const hasSeniorities = filterCnf.prospectFilterPrimarilyFor === 'SENIORITY'

            if (cadences.length > 0 || hasCadence) {
                populationFilters.push({
                    'option': Option.CADENCES,
                    'valueFilterOption': this._getDefaultFilterOption(Option.CADENCES).map((v) => {
                        return {...v, 'selected': cadences.includes(v.value)}
                    })
                })
            }

            if (cadence_steps.length > 0 || hasCadenceStep) {
                populationFilters.push({
                    'option': Option.CADENCE_STEPS,
                    'valueFilterOption': this._getDefaultFilterOption(Option.CADENCE_STEPS).map((v) => {
                        return {...v, 'selected': cadence_steps.includes(v.value)}
                    })
                })
            }


            if (industries.length > 0 || hasIndustry) {
                populationFilters.push({
                    'option': Option.INDUSTRIES,
                    'valueFilterOption': this._getDefaultFilterOption(Option.INDUSTRIES).map((v) => {
                        return {...v, 'selected': industries.includes(v.value)}
                    })
                })
            }

            if (titles.length > 0 || hasTitles) {
                populationFilters.push({
                    'option': Option.TITLES,
                    'valueFilterOption': this._getDefaultFilterOption(Option.TITLES).map((v) => {
                        return {...v, 'selected': titles.includes(v.value)}
                    })
                })
            }

            if (seniorities.length > 0 || hasSeniorities) {
                populationFilters.push({
                    'option': Option.SENIORITIES,
                    'valueFilterOption': this._getDefaultFilterOption(Option.SENIORITIES).map((v) => {
                        return {...v, 'selected': seniorities.includes(v.value)}
                    })
                })
            }
            return populationFilters
        } else if (filterCnf.filter_type === CnfFilterType.REMARK) {
            // remarks
            if (!filterCnf.remark_filters) return populationFilters
            const correspondingRemarks = filterCnf.remark_filters.map((v) => v.remark)
            populationFilters.push({
                'option': Option.REMARK,
                'valueFilterOption': this._getDefaultFilterOption(Option.REMARK).map((v) => {
                    return {
                        ...v, 
                        'selected': correspondingRemarks.includes(v.value as Remark)
                    }
                }), 
                'multiMetadataFilterOption': this._getDefaultMetadataPlayFilterOption(Option.REMARK, filterCnf.lookback_rule, undefined,  filterCnf.lookback_horizon)
            })
        } else if (filterCnf.filter_type === CnfFilterType.PHRASE) {
            // text option
            if (!filterCnf.phrase_filters) return populationFilters
            const partyRolesMatched = filterCnf.phrase_filters.filter((v) => v.party_role).map((v) => v.party_role as PartyRole)
            const partyRoleFilter = filterCnf.phraseFilterPrimarilyFor ? filterCnf.phraseFilterPrimarilyFor === 'PROSPECT' ? PartyRole.PROSPECT : filterCnf.phraseFilterPrimarilyFor === 'REP' ? PartyRole.REP : undefined :  undefined 
            const partyRole = partyRolesMatched.length > 0 ? partyRolesMatched[0] : partyRoleFilter
            const phraseFilters = filterCnf.phrase_filters
            populationFilters.push({
                'option': Option.TEXT,
                'valueFilterOption': phraseFilters.length > 0 ? phraseFilters.map((v) => {
                    return {
                        'label': v.text,
                        'value': v.text,
                        'selected': true,
                    }
                })  : this._getDefaultFilterOption(Option.TEXT),
                'multiMetadataFilterOption': this._getDefaultMetadataPlayFilterOption(Option.TEXT, filterCnf.lookback_rule, partyRole, filterCnf.lookback_horizon)
            })
        } else if (filterCnf.filter_type === CnfFilterType.TIME) {
            if (!filterCnf.time_filters) return populationFilters
            const startNode = filterCnf.time_filters.find((v) => v.start !== undefined)
            const endNode = filterCnf.time_filters.find((v) => v.end !== undefined)
            const preference = filterCnf.timeFilterPrimarilyFor ? filterCnf.timeFilterPrimarilyFor : (startNode && endNode ? TimeFilterType.BETWEEN : startNode ? TimeFilterType.AFTER : endNode? TimeFilterType.BEFORE : undefined) 

            populationFilters.push(
                {
                'option': Option.TIME,
                'valueFilterOption': [{
                    'label': 'START',
                    'selected': preference === TimeFilterType.BEFORE ? false : preference ? true : startNode !== undefined,
                    'value': startNode?.start ? startNode.start.toString() : ''
                }, 
                {
                    'label': 'END',
                    'selected': preference === TimeFilterType.AFTER ? false : preference ? true : endNode !== undefined,
                    'value': endNode?.end ? endNode.end?.toString() : ''
                }],
                'multiMetadataFilterOption': this._getDefaultMetadataPlayFilterOption(Option.TIME, undefined, undefined, undefined, preference)
            })
        } else if (filterCnf.filter_type === CnfFilterType.USER) {
            if (!filterCnf.user_filters) return populationFilters
            const users: string[] = []
            const subteams: string[] = []
            for (const x of filterCnf.user_filters) {
                if (x.user_id) users.push(x.user_id)
                if (x.user_group_id) subteams.push(x.user_group_id)
            }

            populationFilters.push({
                option: Option.USER_IDS,
                valueFilterOption: users.map(userId => {return {'value': userId, 'selected': true, 'label': this.props.visibleAccounts?.users?.find(u => u.user_id === userId)?.user_name ?? "(unknown user)"}}),
                groupsFilterOption: subteams.map(userGroupId => {return {'value': userGroupId, 'selected': true, 'label': this.props.groupIdToGroup.get(userGroupId)?.user_group_name ?? "(unknown team)"}}),
            })
        }
        return populationFilters
    }

    _convertConditionsToPopulationFilters(): PopulationFilter[] {
        if (!this.props.condition.filter_cnf) return []
        return this.props.condition.filter_cnf.map((v) => this._convertFilterTypeToPopulationFilter(v)).flat(1)
    }

    _convertPopulationFilterWithModifiers(populationFilter: PopulationFilter): TemporalFilterDisjunction | null {
        const partyRole = populationFilter.multiMetadataFilterOption?.PARTY_ROLE
        const lookbackRule = populationFilter.multiMetadataFilterOption?.LOOKBACK_RULE
        const timeHorizon = populationFilter.multiMetadataFilterOption?.LOOKBACK_HORIZON
        const timeFilterOption = populationFilter.multiMetadataFilterOption?.TIME_TYPE

        const partyRoleSelectedFound = partyRole ? (partyRole as FilterOption__STRICT[]).find((v) => v.selected) : undefined
        const partyRoleSelected = partyRoleSelectedFound ? partyRoleSelectedFound.value === NO_PARTY_ROLE ? undefined : partyRoleSelectedFound.value as PartyRole : undefined

        const lookbackRuleSelectedFound = lookbackRule ? (lookbackRule as FilterOption__STRICT[]).find((v) => v.selected) : undefined
        const lookbackRuleSelected = lookbackRuleSelectedFound ? lookbackRuleSelectedFound.value === NO_LOOKBACK_RULE ? undefined : lookbackRuleSelectedFound.value as LookbackRule : undefined

        const lookbackHorizon = timeHorizon ? timeHorizon as number : undefined

        if (populationFilter.option === Option.TEXT) {
            // get all the text matches
            return {
                'filter_type': CnfFilterType.PHRASE,
                'phrase_filters': populationFilter.valueFilterOption.filter((v) => v.selected).map((v) => {
                    return {
                        'language': this.getLanguageOption(),
                        'stem': this.getStemmingOption() ?? true,
                        'text': v.value.replaceAll(/[\u00b4\u2018\u2019\u2032\u2035\u275b\u275c]/g, "'").replaceAll(/[\u201c\u201d\u2033\u2036\u275d\u275e]/g, '"'),
                        'party_role': partyRoleSelected,
                    }
                }),
                'phraseFilterPrimarilyFor': partyRoleSelected === 'PROSPECT' ? 'PROSPECT' : partyRoleSelected === PartyRole.REP ? 'REP' : 'ANYONE',
                'lookback_rule': lookbackRuleSelected,
                'lookback_horizon': lookbackHorizon
            }
        } else if (populationFilter.option === Option.REMARK) {
            return {
                'filter_type': CnfFilterType.REMARK,
                'remark_filters': populationFilter.valueFilterOption.filter((v) => v.selected).map((v) => {
                    return {
                        'remark': v.value as Remark,
                        'party_role': undefined
                    }
                }),
                'lookback_rule': lookbackRuleSelected,
                'lookback_horizon': lookbackHorizon,
            }
        } else if (populationFilter.option === Option.TIME) {
            const timeRuleFound = timeFilterOption ? (timeFilterOption as FilterOption__STRICT[]).find((v) => v.selected) : undefined
            const timeRuleSelected = timeRuleFound ? timeRuleFound.value as TimeFilterType : undefined
            const startNode = populationFilter.valueFilterOption.find((v) => v.label === 'START' && v.selected)
            const start = startNode && !isNaN(parseInt(startNode.value)) ? parseInt(startNode?.value) : undefined
            const endNode = populationFilter.valueFilterOption.find((v) => v.label === 'END' && v.selected)
            const end = endNode && !isNaN(parseInt(endNode.value)) ? parseInt(endNode?.value) : undefined
            return {
                'filter_type': CnfFilterType.TIME,
                'time_filters': [{'start': start, 'end': end}],
                'timeFilterPrimarilyFor': timeRuleSelected
            }
        } else if ([Option.CADENCE_STEPS, Option.CADENCES, Option.SENIORITIES, Option.TITLES, Option.INDUSTRIES].includes(populationFilter.option)) {
            const selected = populationFilter.valueFilterOption.filter((v) => v.selected).map((v) => v.value)
            const prospectTempFilter: ProspectFilterTerm[] = selected.map((v) => {
                return {
                    'cadence_step': populationFilter.option === Option.CADENCE_STEPS ? v : undefined,
                    'cadence': populationFilter.option === Option.CADENCES ? v : undefined,
                    'seniority': populationFilter.option === Option.SENIORITIES ? v : undefined,
                    'title': populationFilter.option === Option.TITLES ? v : undefined, 
                    'industry': populationFilter.option === Option.INDUSTRIES ? v : undefined, 
                    'exact': true, 
                    'case_sensitive': true
                }})
            return {
                'filter_type': CnfFilterType.PROSPECT,
                'prospect_filters': prospectTempFilter,
                'prospectFilterPrimarilyFor': populationFilter.option === Option.CADENCE_STEPS ? 'CADENCE_STEP' : 
                populationFilter.option === Option.CADENCES ? 'CADENCE' : 
                populationFilter.option === Option.SENIORITIES ? 'SENIORITY' : 
                populationFilter.option === Option.TITLES ? 'TITLE' : 'INDUSTRY'
                } 
        }
        return null 
    }

    _updatePopulationFilters(filters: PopulationFilter[]) {
        // convert population filter to
        let temporalFilters: (TemporalFilterDisjunction | null)[] = filters.map((v) => this._convertPopulationFilterWithModifiers(v))
        const users: string[] = []
        const subteams: string[] = []
        const userFilters = filters.filter(v => v.option === Option.USER_IDS)
        for (const filter of userFilters) {
            users.push(...filter.valueFilterOption.filter(v => v.selected).map((v) => v.value))
            subteams.push(...(filter.groupsFilterOption?.filter(v => v.selected).map((v) => v.value) ?? []))
        }

        if (userFilters.length > 0) {
            const userFilterTerm: UserFilterTerm[] = [...users.map((v) => {return {'user_id': v}}), ...subteams.map((v) => {return {'user_group_id': v}})]
            
            // find the first user id filter and put it in there..... 
            const filterIdx = filters.findIndex(v => v.option === Option.USER_IDS)
            temporalFilters = [...temporalFilters.slice(0, filterIdx), {
                'filter_type': CnfFilterType.USER,
                'user_filters': userFilterTerm,
            }, ...temporalFilters.slice(filterIdx + 1)]
        }

        temporalFilters = temporalFilters.filter((v) => v)
        this.props.updateCondition({
            //@ts-ignore
            ...this.props.condition, 'filter_cnf': temporalFilters.filter((v) => v)
        })
    }

    _renderPlayOption(play: Play) {
        return <PlayComponent 
            key={play.temp_play_id ?? play.play_id}
            EditableListProps={this._getEditableListProps(play)}
            numberLeft={this.props.plays.length}
            onRemoval={
                () => {
                    const playToRemoveIdx = this.props.plays.findIndex((v) => (v.temp_play_id && v.temp_play_id === play.temp_play_id) || (v.play_id !== _DEFAULT_ID && v.play_id === play.play_id))
                    if (playToRemoveIdx === -1) return null
                    if (this.props.plays[playToRemoveIdx].play_id !== _DEFAULT_ID || this.props.plays[playToRemoveIdx].deployRequested) this.props.updatePlays([...this.props.plays.slice(0, playToRemoveIdx), {...this.props.plays[playToRemoveIdx], 'active': false}, ...this.props.plays.slice(playToRemoveIdx + 1)])
                    else this.props.updatePlays([...this.props.plays.slice(0, playToRemoveIdx), ...this.props.plays.slice(playToRemoveIdx + 1)])
                }
            }
            updatePlayName={(updatedName: string) => {
                const correspondingPlayIdx = this.props.plays.findIndex((v) => (v.temp_play_id && v.temp_play_id === play.temp_play_id) || (v.play_id !== _DEFAULT_ID && v.play_id === play.play_id))
                if (correspondingPlayIdx === -1) return null
                const updatedPlays: Play[] = [...this.props.plays.slice(0, correspondingPlayIdx), {...play, 'play_name': updatedName}, ...this.props.plays.slice(correspondingPlayIdx + 1)]
                this.props.updatePlays(updatedPlays)
           }} 
           play={play}
        />
    }

    _addPlay() {
        this.props.updatePlays([...this.props.plays, {...constructDefaultPlay(this.props.condition, this.props.totalPlays + 1, this.props.plays), 'weight': 0}])
    }

    _renderSyncedStatus(): JSX.Element | null{
        if ([SyncStatus.SAVED].includes(this.props.syncedStatus)) {
            return <div className="w-5 h-5 flex flex-row justify-center items-center bg-green-500 rounded-full">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>
        }
        else if ([SyncStatus.PENDING].includes(this.props.syncedStatus)) {
            return <div className="w-5 h-5 bg-yellow-400 rounded-full">
            </div>
        } else if ([SyncStatus.HAS_ERRORS].includes(this.props.syncedStatus)) {
            return <div onMouseEnter={() => this.setState({'showErrors': true})} onMouseLeave={() => this.setState({'showErrors': false})}
            className="pl-2 pr-2 h-5 cursor-pointer flex flex-row justify-center items-center text-center bg-red-100 border border-red-300 hover:bg-red-200 hover:border-red-400 transition-colors duration-200" style={{'borderRadius': '8px'}}>
                <Typography variant="largeParagraph" color="#DC2626" fontWeight={600}>
                    {`${this.props.syncErrors && this.props.syncErrors.length > 0 ? `Fix errors (${this.props.syncErrors.length})` : 'Fix errors!'}`}
                </Typography>
                <div className="relative w-0 h-0">
                    {this.state.showErrors && this.props.syncErrors ? <div className="absolute right-0 w-fit p-2 flex flex-col gap-2 text-left bg-red-50 border border-red-200 shadow-lg" style={{
                        'bottom': '15px',
                        'zIndex': 999999,
                        'borderRadius': '8px',
                    }}>
                        {this.props.syncErrors.map((v, idx) => {return <Typography key={v} variant="largeParagraph" color="#DC2626" style={{'whiteSpace': 'nowrap', 'textAlign': 'left'}}>
                           {`${idx + 1}. ${v}`}
                        </Typography>})}
                    </div> : null}
                </div>
            </div>
        } else if ([SyncStatus.READY_TO_DEPLOY].includes(this.props.syncedStatus)) {
            return <div onClick={this.props.onForceSave.bind(this)}
            className="pl-2 pr-2 h-5 cursor-pointer flex flex-row justify-center items-center text-center bg-blue-100 border border-blue-300 hover:bg-blue-200 hover:border-blue-400 transition-colors duration-200" 
            style={{'borderRadius': '8px'}}>
                <Typography variant="largeParagraph" color="#1E40AF" fontWeight={600}>
                    {this.props.condition.active ? "Click to deploy" : "Click to activate"}
                </Typography>
            </div>
        } else if ([SyncStatus.DEPLOYING].includes(this.props.syncedStatus)) {
            return <div onClick={this.props.onForceSave.bind(this)}
            className="pl-2 pr-2 h-5 flex flex-row justify-center items-center text-center bg-gray-100 border border-gray-300" 
            style={{'borderRadius': '8px'}}>
                <Typography variant="largeParagraph" color="#6B7280" fontWeight={600}>
                    Deploying!
                </Typography>
            </div>
        }
        return null
    }

    _getDuration(): number {
        const durationNode = this.props.plays.find((v) => v.min_duration !== null)
        return durationNode?.min_duration ?? _DEFAULT_DURATION_TIME
    }
 
    _renderSuggestionBody(): JSX.Element {
        const duration = this._getDuration()
        return (
        <div className='flex flex-col bg-white rounded-lg' style={{'minWidth': '328px'}}>
            <div className="h-full flex flex-col"
                style={{'zIndex': 0} /* prevent popping over sticky elements like "New Play". Assumes descendants will not overflow. */}>
            
            <div className="w-full flex flex-row pt-3 pr-3 pl-3 gap-2 items-center border-b border-gray-200 pb-2">
                <Typography variant='h4' color="#374151" fontWeight={600}>
                {'Rep sees for:'}
            </Typography> 
            <div className="flex flex-row gap-2 items-center">
                <input 
                onChange={(e) => {
                    let number = parseInt(e.target.value)
                    if (isNaN(number)) number = 0
                    this.props.updatePlays([...this.props.plays].map((v) => {return {...v, 'min_duration': number}}))
                }}
                type="number" 
                    className="border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={
                    {
                        'width': '50px',
                        'lineHeight': '0.75em',
                        'fontSize': '12px'
                    }
                }
                value={duration ?? 0}
                min={3} max={100} />
                    <Typography variant="largeParagraph" color="#374151" style={{'whiteSpace': 'nowrap'}}>
                    (s)
                </Typography>
            </div>
            </div>
            
            <div className="flex flex-col gap-2 p-3 pb-2">
            {this.props.plays.map((v) => this._renderPlayOption(v))}
            </div>
        </div>
        </div>)
    }

    getLanguageOption() {
        const phraseFilters = this.props.condition.filter_cnf.filter((v) => v.filter_type === CnfFilterType.PHRASE && v.phrase_filters)
        const languageOptions = phraseFilters.map((v) => (v.phrase_filters ?? []).map((x) => x.language).flat(1)).flat(1)
        return languageOptions.length > 0 ? languageOptions[0] : DEFAULT_LANGUAGE_SETTING.ENGLISH
    }

    canShowLanguage(): boolean {
        return this.props.visibleAccounts !== null && this.props.visibleAccounts.users.length > 0 && ELIGIBLE_FOR_SPANISH.includes(this.props.visibleAccounts.users[0].team_id) && this.props.condition.filter_cnf.find((v) => v.filter_type === CnfFilterType.PHRASE) !== undefined
    }

    getStemmingOption(): boolean | undefined {
        const phraseFilters = this.props.condition.filter_cnf.filter((v) => v.filter_type === CnfFilterType.PHRASE && v.phrase_filters && v.phrase_filters.length > 0).map((v) => (v.phrase_filters ?? [])).flat(1)
        if (phraseFilters.length === 0) return undefined
        const nonStemming = phraseFilters.find((v) => !v.stem) !== undefined
        return !nonStemming
    }

    render() {
        const languageOption = this.getLanguageOption()
        return <div className="w-full h-fit flex flex-row gap-4" 
        onMouseEnter={() => this.setState({'showRemove': true})}
        onMouseLeave={() => this.setState({'showRemove': false})}
        style={{'maxWidth': '1200px'}}>
            <FilterSelector
            showMatches={false}
            canShowOption={[Option.USER_IDS, Option.TEXT, Option.TIME, ...PROSPECT_OPTIONS]}
            populationFilters={this._convertConditionsToPopulationFilters()}
            updatePopulationFilters={this._updatePopulationFilters.bind(this)}
            isCoachingView={false}
            viewType={ViewType.TRIGGERS}
            noTextDelay={true}
            startWithExpandedFilter={this.props.condition.condition_id === _DEFAULT_ID}
            conditionName={this.props.condition.condition_name ?? undefined}
            updateConditionName={(name: string) => this.props.updateCondition({...this.props.condition, 'condition_name': name})}
            canShowLanguageOption={this.canShowLanguage()}
            updateLanguageOption={(updatedFilters: FilterOption[]) => {
                const selected = updatedFilters.find((x) => x.selected)
                if (!selected) return
                const newLanguage = selected.value as DEFAULT_LANGUAGE_SETTING 
                const updatedCnf = this.props.condition.filter_cnf.map((v) => {
                    if (v.filter_type !== CnfFilterType.PHRASE || !v.phrase_filters) return v
                    return {...v, 'phrase_filters': v.phrase_filters.map((x) => {return {...x, 'language': newLanguage}})}
                })
                this.props.updateCondition({...this.props.condition, 'filter_cnf': updatedCnf})
            }}
            stemmingOption={this.getStemmingOption()}
            updateStemmingOption={(value: boolean) => {
                const updatedCnf = this.props.condition.filter_cnf.map((v) => {
                    if (v.filter_type !== CnfFilterType.PHRASE || !v.phrase_filters) return v
                    return {...v, 'phrase_filters': v.phrase_filters.map((x) => {return {...x, 'stem': value}})}
                })
                this.props.updateCondition({...this.props.condition, 'filter_cnf': updatedCnf})
            }}
            languageOption={Object.values(DEFAULT_LANGUAGE_SETTING).map((v) => {return {'label': LANGUAGE_SETTING_TO_HUMAN_READABLE[v], 'value': v, 'selected': v === languageOption}})}
            />
            {this._renderSuggestionBody()}
        </div>
    }
}

const PlayConditionComponent = connect((state: RootState) => {
    return {
        visibleAccounts: convertFromReduxSafeVisibleAccounts(state.visibleAccounts, (u) => u.can_dial && u.team_is_active),
        prospectOptions: state.prospectInfoOptions.value,
    }
})(PlayConditionComponentImpl)

enum TabOption {
    ACTIVE = 'Active',
    INACTIVE = 'Inactive'
}

export type PlayState = {
    plays: Play[] | null
    conditions: Condition[] | null

    savedConditions: Condition[] | null
    savedPlays: Play[] | null
    counter: number

    showTranscript: boolean
    selectedCondition?: Condition

    totalPossibleMatches?: number
    selectedTab: TabOption
    expandedConditions: Set<string> // Track which conditions are expanded
    hoveredConditionId?: string // Track which condition is being hovered for removal
}

type PlaysProps<A extends Action = AnyAction> = {
    dispatch: Dispatch<A>;
    groupIdToGroup: Map<string, UserGroup>
    groupsHasLoaded: boolean
    visibleAccounts: VisibleAccountsResult | null
}

class Plays extends React.Component<PlaysProps, PlayState> {
    constructor(props: PlaysProps) {
        super(props)
        this.state = {
            plays: null,
            conditions: null,
            savedConditions: null,
            savedPlays: null,
            counter: 0,
            showTranscript: false,
            selectedTab: TabOption.ACTIVE,
            expandedConditions: new Set(),
            hoveredConditionId: undefined
        }
    }

    componentDidUpdate(prevProps: Readonly<PlaysProps>, prevState: Readonly<PlayState>, snapshot?: any): void {
        if (this.state.counter - prevState.counter === 1) {
            this._onSave()
        }
    }

    _jsonifyParams(params: any) {
        return Array.from(Object.entries({...params, 'deployRequested': undefined})).map(([key, value]) => JSON.stringify(value)).join('')
    }

    _updateCounter() {
        this.setState((state) => {return {'counter': state.counter + 1}})
    }

    async _onSave() {
        await sleep(2000)
        if (!this.state.conditions || !this.state.plays || !this.state.savedPlays || !this.state.savedConditions) {
            this._updateCounter()
            return 
        }// get the conditions and play
        
        // check if there are any pending conditions that are ready to be saved and deploy has been requested (?) 
        // save anything with a pending tag
        const newConditions: Condition[] = []
        const updatedConditions: Condition[] = []

        const conditionStates = this.state.conditions.map((v) => this._getConditionSyncStatus(v))
        for (let i = 0; i < conditionStates.length; i++) {
            // find matching condition in the saved condition
            const conditionState = conditionStates[i]
            if ([SyncStatus.DEPLOYING].includes(conditionState.status)) {
                newConditions.push(this.state.conditions[i])
            } else if ([SyncStatus.PENDING].includes(conditionState.status)) {
                updatedConditions.push(this.state.conditions[i])
            }
        }

        const tempConditionIdToTrueConditionId: {[k: string]: string} = {}

        for (const newCondition of newConditions) {
            const newConditionUpdated = await getServicesManager().putCondition(null, newCondition.filter_cnf, newCondition.active, newCondition.condition_name, newCondition.condition_group)
            if (!newConditionUpdated) {
                // you errored out
                return 
            }
            if (newCondition.temp_condition_id) tempConditionIdToTrueConditionId[newCondition.temp_condition_id] = newConditionUpdated.condition_id
        }

        for (const updatedCondition of updatedConditions) {
            const updatedConditionResult = await getServicesManager().putCondition(updatedCondition.condition_id, updatedCondition.filter_cnf, updatedCondition.active, updatedCondition.condition_name, updatedCondition.condition_group)
            if (!updatedConditionResult) {
                return
            }
        }

        // given we now have saved some conditions, we can basically save any plays that have deploying

        const newPlays: Play[] = []
        const updatedPlays: Play[] = []

        const playStates = this.state.plays.map((v) => this._getPlaySyncStatus(v))
        for (let i = 0; i < playStates.length; i++) {
            const playState = playStates[i]
            const play = this.state.plays[i]
            if ([SyncStatus.DEPLOYING].includes(playState.status)) {
                // if it's in a deploying state 
                const correspondingCondition = this.state.conditions.find((v) => (v.condition_id !== _DEFAULT_ID && v.condition_id === play.condition_id) || (v.temp_condition_id && v.temp_condition_id === play.temp_condition_id))
                if (!correspondingCondition || 
                    (correspondingCondition.condition_id === _DEFAULT_ID && correspondingCondition.temp_condition_id && !tempConditionIdToTrueConditionId[correspondingCondition.temp_condition_id])) {
                        continue
                } 
                newPlays.push(play)
            } else if ([SyncStatus.PENDING].includes(playState.status)) {
                updatedPlays.push(play)
            }
        }


        const tempPlayIdToTruePlayId: {[k: string]: string} = {}
        
        for (const newPlay of newPlays) {
            const correspondingCondition = this.state.conditions.find((v) => (v.condition_id !== _DEFAULT_ID && v.condition_id === newPlay.condition_id) || (v.temp_condition_id && v.temp_condition_id === newPlay.temp_condition_id))
            if (!correspondingCondition) continue
            const conditionId = correspondingCondition.condition_id !== _DEFAULT_ID ? correspondingCondition.condition_id : correspondingCondition.temp_condition_id ? tempConditionIdToTrueConditionId[correspondingCondition.temp_condition_id] : undefined
            if (!conditionId) continue
            const newPlayUpdated = await getServicesManager().putPlay(null, conditionId, newPlay.active, newPlay.prompt_text, newPlay.weight, newPlay.play_name, newPlay.min_duration)
            if (!newPlayUpdated) {
                return
            }
            if (newPlay.temp_play_id) tempPlayIdToTruePlayId[newPlay.temp_play_id] = newPlayUpdated.play_id
        }

        for (const updatedPlay of updatedPlays) {
            const updatedPlayResult = await getServicesManager().putPlay(updatedPlay.play_id, updatedPlay.condition_id, updatedPlay.active, updatedPlay.prompt_text, updatedPlay.weight, updatedPlay.play_name, updatedPlay.min_duration)
            if (!updatedPlayResult) {
                return
            }
        }

        this.setState((state) => {
            if (!state.conditions || !state.savedConditions || !state.plays || !state.savedPlays) return null
            // need to adjust ids for anything where we made a new id 
            // for modifications we need to copy the new values in ... 
            const newConditionIds = newConditions.map((v) => v.temp_condition_id)
            const newPlayIds = newPlays.map((v) => v.temp_play_id ?? '')

            const update_conditions: Condition[] = [...state.conditions].map((v) => {
                return v.temp_condition_id && newConditionIds.includes(v.temp_condition_id) ? {...v, 'condition_id': tempConditionIdToTrueConditionId[v.temp_condition_id]} : v
            })

            const update_play: Play[] = [...state.plays].map((v) => {
                return {
                    ...v, 
                    'condition_id': v.temp_condition_id && newConditionIds.includes(v.temp_condition_id) ? tempConditionIdToTrueConditionId[v.temp_condition_id] : v.condition_id, 
                    'play_id': v.temp_play_id && newPlayIds.includes(v.temp_play_id) ? tempPlayIdToTruePlayId[v.temp_play_id] : v.play_id 
                }
            })

            // need to add the new one and add modifications
            const updated_saved_conditions: Condition[] = [...state.savedConditions, ...newConditions].map((v) => {
                const conditionId =  v.temp_condition_id && newConditionIds.includes(v.temp_condition_id) ? tempConditionIdToTrueConditionId[v.temp_condition_id] : v.condition_id
                const modifiedCondition = updatedConditions.find((x) => x.condition_id === v.condition_id)
                if (modifiedCondition) {
                    return {...v, ...modifiedCondition, 'condition_id': conditionId}
                } else {
                    return {...v, 'condition_id': conditionId}
                }
            })

            const updated_saved_plays: Play[] = [...state.savedPlays, ...newPlays].map((v) => {
                const conditionId =  v.temp_condition_id && newConditionIds.includes(v.temp_condition_id) ? tempConditionIdToTrueConditionId[v.temp_condition_id] : v.condition_id
                const playId = v.temp_play_id && newPlayIds.includes(v.temp_play_id) ? tempPlayIdToTruePlayId[v.temp_play_id] : v.play_id

                const modifiedPlay = updatedPlays.find((x) => x.play_id === v.play_id)
                
                if (modifiedPlay) {
                    return {...v, ...modifiedPlay, 'play_id': playId, 'condition_id': conditionId}
                } else {
                    return {...v, 'play_id': playId, 'condition_id': conditionId}
                }
            })

            return {
                'conditions': update_conditions,
                'plays': update_play,
                'savedConditions': updated_saved_conditions,
                'savedPlays': updated_saved_plays,
                'counter': state.counter + 1
            }
        })
    }

    _getPlaySyncStatus(play: Play): {'status': SyncStatus, 'errors'?: string[]} {
        if (!this.state.conditions || !this.state.plays || !this.state.savedConditions || !this.state.savedPlays) return {'status': SyncStatus.HAS_ERRORS, 'errors': ['Still loading data...']}
        const correspondingConditionSaved = this.state.savedConditions.find((v) => (v.condition_id !== _DEFAULT_ID && v.condition_id === play.condition_id))
        // if there is no saved corresponding condition, check if its because the matching condition has errors 
        if (!correspondingConditionSaved) {
            const correspondingConditionMatched = this.state.conditions.find((v) => (v.condition_id !== _DEFAULT_ID && v.condition_id === play.condition_id) || (v.temp_condition_id && v.temp_condition_id === play.temp_condition_id))
            if (!correspondingConditionMatched) return {'status': SyncStatus.HAS_ERRORS, 'errors': ['Play has no matching condition']}
        }

        // check if its in the saved plays
        const matchingSavedPlay = this.state.savedPlays.find((v) => (v.play_id !== _DEFAULT_ID && v.play_id === play.play_id))
        if (!matchingSavedPlay || 
            this._jsonifyParams(matchingSavedPlay) !== this._jsonifyParams(play)) {
            // figure out if its a pending issue or if we need to actually fix this thing... 
            if (play.prompt_text.split('\n').every((v) => v === '')) {
                return {'status': SyncStatus.HAS_ERRORS, 'errors': [`${play.play_name} is missing suggestions`]}
            } else {
                return {'status': play.play_id !== _DEFAULT_ID ? SyncStatus.PENDING : play.deployRequested ? SyncStatus.DEPLOYING : SyncStatus.READY_TO_DEPLOY} 
            }
        } else {
            return {'status': SyncStatus.SAVED}
        }
    } 

    _errorCheckCnf(filters: TemporalFilterDisjunction[]): string[] {
        const errors = []
        if (filters.length === 0) {
            errors.push('Requires a filter for condition to work')
        }

        for (const filter of filters) {
            if (filter.filter_type === CnfFilterType.USER) {
                if (!filter.user_filters || filter.user_filters.length === 0 || filter.user_filters.some((v) => !v.user_group_id && !v.user_id)) {
                    errors.push('Must select an option for users or subteams')
                }
            } else if (filter.filter_type === CnfFilterType.PROSPECT) {
                if (!filter.prospect_filters || filter.prospect_filters.length === 0 || filter.prospect_filters.some((v) => !v.cadence_step && !v.cadence && !v.industry && !v.seniority && !v.title)) {
                    errors.push(`Must select an option for ${filter.prospectFilterPrimarilyFor === 'CADENCE_STEP' ? 'cadence step' : filter.prospectFilterPrimarilyFor === 'CADENCE' ? 'cadence' : filter.prospectFilterPrimarilyFor === 'INDUSTRY' ? 'industry' : filter.prospectFilterPrimarilyFor === 'SENIORITY' ? 'seniority' : filter.prospectFilterPrimarilyFor === 'TITLE' ? 'title' : 'prospect filter'}`)
                }
            } else if (filter.filter_type === CnfFilterType.TIME) {
                if (!filter.time_filters || filter.time_filters.length === 0) errors.push('Requires a time filter')
                if (filter.time_filters && filter.time_filters.some((v) => v.start === undefined && v.end === undefined)) errors.push('Must provide a time option')
                if (filter.time_filters && filter.time_filters.some((v) => v.start && v.end && v.start >= v.end)) errors.push('Start time must be less than end time')
            } else if (filter.filter_type === CnfFilterType.PHRASE) {
                if (!filter.phrase_filters || filter.phrase_filters.length === 0) errors.push('Requires a phrase filter')
                if (filter.phrase_filters && filter.phrase_filters.map((v) => v.text).find((v) => v === '') !== undefined) errors.push('Must provide a valid text filter - no empty strings') 
            } else if (filter.filter_type === CnfFilterType.REMARK) {
                if (!filter.remark_filters || filter.remark_filters.length === 0) errors.push('Requires a remark filter')
            }
            if (filter.lookback_rule !== undefined && ![CnfFilterType.PHRASE, CnfFilterType.REMARK].includes(filter.filter_type)) {
                errors.push('Cannot apply a lookback for non-phrase/remark filters')
            }

            if (filter.lookback_horizon !== undefined && !filter.lookback_rule) {
                errors.push('Cannot specify a lookback horizon')
            }
        }
        return errors
    }

    _getConditionSyncStatus(condition: Condition): {'status': SyncStatus ,'errors'?: string[]} {
        if (!this.state.conditions || !this.state.plays || !this.state.savedConditions || !this.state.savedPlays) return {'status': SyncStatus.HAS_ERRORS, 'errors': ['Still loading data...']}
        const correspondingCondition = this.state.savedConditions.find((v) => (v.condition_id !== _DEFAULT_ID && v.condition_id === condition.condition_id))
        if (!correspondingCondition || 
            this._jsonifyParams({...correspondingCondition}) !== this._jsonifyParams({...condition})) {
            // check if the condition is ok to be saved 
            const errors = this._errorCheckCnf(condition.filter_cnf)
            if (errors.length > 0) return {'status': SyncStatus.HAS_ERRORS, 'errors': errors}
            else return {'status': condition.condition_id !== _DEFAULT_ID ? SyncStatus.PENDING : condition.deployRequested ? SyncStatus.DEPLOYING : SyncStatus.READY_TO_DEPLOY}
        } else {

            return {'status': SyncStatus.SAVED}
        }
    }

    _calculateSyncStatusAndPotentialErrors(condition: Condition, plays: Play[]): {'status': SyncStatus, 'errors'?: string[]} {
        // check if condition is ready to be saved --- if not err
        const conditionStatus = this._getConditionSyncStatus(condition)
        let playStatus = []
        if (plays.length === 0) {
            playStatus = [{'status': SyncStatus.HAS_ERRORS, 'errors': ['Missing suggestion']}]
        }
        else {
            playStatus = plays.map((v) => this._getPlaySyncStatus(v))
        }

        // if all of them are pending.... 
        const allStatuses = [conditionStatus, ...playStatus]

        // any errors? 
        const errorStatuses = allStatuses.filter((v) => v.status === SyncStatus.HAS_ERRORS)
        if (errorStatuses.length > 0) {
            const errors: string[] = errorStatuses.map((v) => (v.errors ?? [])).flat(1)
            return {'status': SyncStatus.HAS_ERRORS, 'errors': errors}
        }
        
        const pending = allStatuses.find((v) => v.status === SyncStatus.PENDING)
        if (pending !== undefined) return {'status': SyncStatus.PENDING}

        const readyToDeploy = allStatuses.find((v) => v.status === SyncStatus.READY_TO_DEPLOY)
        if (readyToDeploy !== undefined) return {'status': SyncStatus.READY_TO_DEPLOY}

        const deploying = allStatuses.find((v) => v.status === SyncStatus.DEPLOYING)
        if (deploying !== undefined) return {'status': SyncStatus.DEPLOYING}

        return {'status': SyncStatus.SAVED}
    }

    componentDidMount(): void {
        getServicesManager().getPlays().then((v) => {
            this.setState({'plays': v, 'savedPlays': v})
        })

        getServicesManager().getConditions().then((v) => {
            const conditions = v
            if (conditions) conditions.sort((a, b) => a.created_at.getTime() - b.created_at.getTime())
            this.setState({'conditions': conditions, 'savedConditions': conditions})
        })

        this.setState({'counter': 1})
    }

    _handleConditionClickFromSidebar(conditionId: string) {
        // Toggle expansion
        this._toggleConditionExpansion(conditionId)

        // Scroll to condition after a small delay to ensure it's rendered
        setTimeout(() => {
            const element = document.getElementById(`condition-${conditionId}`)
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
        }, 100)
    }

    _addNewCondition() {
        this.setState((state) => {
            if (!state.plays || !state.conditions) return null
            const newCondition: Condition = constructDefaultCondition(state.conditions.length + 1)
            const newPlay: Play =  constructDefaultPlay(newCondition, state.plays.length + 1, [])
            const conditionId = newCondition.temp_condition_id ?? newCondition.condition_id
            const newExpanded = new Set(state.expandedConditions)
            newExpanded.add(conditionId)
            return {
                'conditions': [...state.conditions, newCondition],
                'plays': [...state.plays, newPlay],
                'expandedConditions': newExpanded
            }
        })
    } 

    _addNewOpener() {
        this.setState((state) => {
            if (!state.plays || !state.conditions) return null
            const newCondition: Condition = constructDefaultCondition(state.conditions.length + 1)
            newCondition.filter_cnf = [{'filter_type': CnfFilterType.TIME, 'timeFilterPrimarilyFor': TimeFilterType.BEFORE, 'time_filters': [{'start': undefined, 'end': 40}]}]
            const newPlay: Play =  constructDefaultPlay(newCondition, state.plays.length + 1, [])
            const conditionId = newCondition.temp_condition_id ?? newCondition.condition_id
            const newExpanded = new Set(state.expandedConditions)
            newExpanded.add(conditionId)
            return {
                'conditions': [...state.conditions, newCondition], 
                'plays': [...state.plays, newPlay],
                'expandedConditions': newExpanded
            }
        })
    }

    _hasPending() {
        if (!this.state.conditions || !this.state.plays || !this.state.savedConditions || !this.state.savedPlays) return false
        const conditionStatus = this.state.conditions.find((v) => {
            const status = this._getConditionSyncStatus(v)
            if ([SyncStatus.PENDING, SyncStatus.DEPLOYING, SyncStatus.HAS_ERRORS].includes(status.status)) return true
            if (status.status === SyncStatus.READY_TO_DEPLOY && v.condition_id === _DEFAULT_ID) return true
            return false
         })
         if (conditionStatus) return true 

         const playStatus = this.state.plays.find((v) => {
            const status = this._getPlaySyncStatus(v)
            if ([SyncStatus.PENDING, SyncStatus.DEPLOYING, SyncStatus.HAS_ERRORS].includes(status.status)) return true
            if (status.status === SyncStatus.READY_TO_DEPLOY && v.play_id === _DEFAULT_ID) return true
            return false
         })
         if (playStatus) return true
         return false 
    }

    _toggleConditionExpansion(conditionId: string) {
        this.setState((state) => {
            const newExpanded = new Set(state.expandedConditions)
            if (newExpanded.has(conditionId)) {
                newExpanded.delete(conditionId)
            } else {
                newExpanded.add(conditionId)
            }
            return { expandedConditions: newExpanded }
        })
    }

    _isConditionExpanded(conditionId: string): boolean {
        return this.state.expandedConditions.has(conditionId)
    }

    _getQuickFilterDescription(condition: Condition): string {
        if (!condition.filter_cnf || condition.filter_cnf.length === 0) return "No filters"
        
        const descriptions: string[] = []
        for (const filter of condition.filter_cnf) {
            if (filter.filter_type === CnfFilterType.TIME && filter.time_filters) {
                const timeFilter = filter.time_filters[0]
                if (timeFilter.start && timeFilter.end) {
                    descriptions.push(`${timeFilter.start}-${timeFilter.end}s`)
                } else if (timeFilter.end) {
                    descriptions.push(`Before ${timeFilter.end}s`)
                } else if (timeFilter.start) {
                    descriptions.push(`After ${timeFilter.start}s`)
                }
            } else if (filter.filter_type === CnfFilterType.PHRASE && filter.phrase_filters) {
                const phrases = filter.phrase_filters.slice(0, 2).map(p => `"${p.text}"`).join(", ")
                descriptions.push(filter.phrase_filters.length > 2 ? `${phrases} +${filter.phrase_filters.length - 2} more` : phrases)
            } else if (filter.filter_type === CnfFilterType.USER && filter.user_filters) {
                descriptions.push(`${filter.user_filters.length} user${filter.user_filters.length > 1 ? 's' : ''}`)
            } else if (filter.filter_type === CnfFilterType.PROSPECT && filter.prospect_filters) {
                const prospectFilterType = filter.prospectFilterPrimarilyFor
                let filterTypeName = "prospect"
                if (prospectFilterType === 'CADENCE') filterTypeName = "cadence"
                else if (prospectFilterType === 'CADENCE_STEP') filterTypeName = "cadence step"
                else if (prospectFilterType === 'INDUSTRY') filterTypeName = "industry"
                else if (prospectFilterType === 'TITLE') filterTypeName = "title"
                else if (prospectFilterType === 'SENIORITY') filterTypeName = "seniority"
                
                const values = filter.prospect_filters.slice(0, 2).map(p => {
                    if (prospectFilterType === 'CADENCE') return p.cadence
                    else if (prospectFilterType === 'CADENCE_STEP') return p.cadence_step
                    else if (prospectFilterType === 'INDUSTRY') return p.industry
                    else if (prospectFilterType === 'TITLE') return p.title
                    else if (prospectFilterType === 'SENIORITY') return p.seniority
                    return ""
                }).filter(v => v).join(", ")
                
                descriptions.push(filter.prospect_filters.length > 2 ? `${filterTypeName}: ${values}...` : `${filterTypeName}: ${values}`)
            } else if (filter.filter_type === CnfFilterType.REMARK && filter.remark_filters) {
                const remarkNames = filter.remark_filters.slice(0, 3).map(r => SHORT_REMARK_MAPPING[r.remark] || REMARK_TO_HUMAN_READABLE[r.remark]).join(", ")
                descriptions.push(filter.remark_filters.length > 3 ? `${remarkNames}...` : remarkNames)
            }
        }
        return descriptions.length > 0 ? descriptions.join(" • ") : "Filters configured"
    }

    renderCondition(v: Condition): JSX.Element | null {
        const correspondingPlays = this.state.plays?.filter((x) => x.active && ((x.temp_condition_id && x.temp_condition_id === v.temp_condition_id) || (v.condition_id !== _DEFAULT_ID && v.condition_id === x.condition_id))) ?? []
        const correspondingConditionId = v.condition_id !== _DEFAULT_ID ? v.condition_id : v.temp_condition_id
        if (!correspondingConditionId) return null
        const status = this._calculateSyncStatusAndPotentialErrors(v, correspondingPlays)
        const isExpanded = this._isConditionExpanded(correspondingConditionId)
        const isHovered = this.state.hoveredConditionId === correspondingConditionId
        
        // Get suggestion name if available
        const suggestionName = correspondingPlays.length > 0 ? correspondingPlays[0].play_name : null
        const filterDescription = this._getQuickFilterDescription(v)
        
        // Minimized view
        if (!isExpanded) {
            return <div
        key={correspondingConditionId}
        id={`condition-${correspondingConditionId}`}
                className="w-full max-w-4xl bg-white border border-gray-200 hover:border-gray-300 rounded-lg shadow-sm transition-colors duration-200 p-4 relative"
                onMouseEnter={() => this.setState({hoveredConditionId: correspondingConditionId})}
                onMouseLeave={() => this.setState({hoveredConditionId: undefined})}
            >
                <div className="flex items-center justify-between min-w-0">
                    <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                        <button 
                            onClick={() => this._toggleConditionExpansion(correspondingConditionId)}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors duration-200 shadow-sm flex-shrink-0"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-white">
                                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="flex items-center gap-2 text-sm text-gray-600 overflow-hidden max-w-[600px]">
                                <span className="truncate flex-shrink-0 font-semibold text-gray-800">
                                    {v.condition_name || `Condition #${correspondingConditionId}`}
                                </span>
                                {suggestionName && (
                                    <>
                                        <span className="text-gray-400 flex-shrink-0">|</span>
                                        <span className="truncate flex-shrink min-w-0 text-gray-600">
                                            {suggestionName}
                                        </span>
                                    </>
                                )}
                                <span className="text-gray-400 flex-shrink-0">|</span>
                                <span className="truncate flex-shrink min-w-0 text-gray-500 text-xs">
                                    {filterDescription}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                        {status.status === SyncStatus.SAVED && (
                            <div className="w-5 h-5 flex items-center justify-center bg-green-500 rounded-full">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
                                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        )}
                        {status.status === SyncStatus.PENDING && (
                            <div className="w-5 h-5 bg-yellow-400 rounded-full"></div>
                        )}
                        {status.status === SyncStatus.HAS_ERRORS && (
                            <div className="relative group">
                                <div className="w-7 h-7 bg-red-500 hover:bg-red-600 border-2 border-red-300 hover:border-red-400 rounded-full cursor-pointer flex items-center justify-center transition-colors duration-200 shadow-sm" title={status.errors?.join(', ') || 'Has errors'}>
                                    <Typography variant="smallParagraph" color="white" fontWeight={700} className="text-xs leading-none">
                                        {status.errors?.length || '!'}
                                    </Typography>
                                </div>
                                {status.errors && status.errors.length > 0 && (
                                    <div className="absolute right-0 top-8 w-72 p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg text-xs z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                        <Typography variant="smallParagraph" color="#DC2626" fontWeight={600} className="mb-2">
                                            {status.errors.length} Error{status.errors.length > 1 ? 's' : ''} Found:
                                        </Typography>
                                        {status.errors.map((error, idx) => (
                                            <div key={idx} className="text-red-700 mb-1 leading-relaxed">• {error}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {isHovered && (
                            <button 
                                onClick={() => {
            this.setState((state) => {
                if (!state.conditions || !state.plays || !correspondingConditionId) return null
                const correspondingConditionIdx = state.conditions.findIndex((v) => (v.condition_id !== _DEFAULT_ID && v.condition_id === correspondingConditionId) || (v.temp_condition_id && v.temp_condition_id === correspondingConditionId))
                if (correspondingConditionIdx === -1) return null
                const updatedPlays = [...state.plays].filter((x) => {
                    if ((x.temp_condition_id && x.temp_condition_id === v.temp_condition_id) || (v.condition_id !== _DEFAULT_ID && v.condition_id === x.condition_id)) {
                        if (x.play_id === _DEFAULT_ID && !x.deployRequested) return false
                    } 
                    return true
                }).map((x) => {
                    if (x.temp_condition_id && x.temp_condition_id === v.temp_condition_id || (v.condition_id !== _DEFAULT_ID && v.condition_id === x.condition_id)) return {...x, 'active': false}
                    return {...x}
                })

                let updatedConditions: Condition[] = []
                const correspondingCondition = state.conditions[correspondingConditionIdx]
                if (correspondingCondition.condition_id !== _DEFAULT_ID || correspondingCondition.deployRequested) {
                    updatedConditions = [...state.conditions.slice(0, correspondingConditionIdx), {...state.conditions[correspondingConditionIdx], 'active': false}, ...state.conditions.slice(correspondingConditionIdx + 1)]
                } else {
                    updatedConditions = [...state.conditions.slice(0, correspondingConditionIdx), ...state.conditions.slice(correspondingConditionIdx + 1)]
                }

                return {
                    'conditions': [...state.conditions.slice(0, correspondingConditionIdx), {...state.conditions[correspondingConditionIdx], 'active': false}, ...state.conditions.slice(correspondingConditionIdx + 1)],
                    'plays': updatedPlays,
                }
            })
        }}
                                className="flex items-center justify-center w-6 h-6 rounded-full bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 transition-colors duration-200"
                            >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-red-600">
                                    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        }

        // Expanded view
        return <div key={correspondingConditionId} id={`condition-${correspondingConditionId}`} className="w-full max-w-4xl bg-white rounded-lg shadow-sm border border-gray-200 transition-colors duration-200">
            <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button 
                            onClick={() => this._toggleConditionExpansion(correspondingConditionId)}
                            className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 text-white transition-colors duration-200 shadow-sm"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-white">
                                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <Typography variant="h4" color="#374151" fontWeight={600}>
                            {v.condition_name || `Condition #${correspondingConditionId}`}
                        </Typography>
                    </div>
                    <div className="flex items-center gap-3">
                        {status.status === SyncStatus.SAVED && (
                            <div className="w-5 h-5 flex items-center justify-center bg-green-500 rounded-full">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-white">
                                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </div>
                        )}
                        {status.status === SyncStatus.PENDING && (
                            <div className="w-5 h-5 bg-yellow-400 rounded-full"></div>
                        )}
                        {status.status === SyncStatus.HAS_ERRORS && (
                            <div className="relative group">
                                <div className="w-7 h-7 bg-red-500 hover:bg-red-600 border-2 border-red-300 hover:border-red-400 rounded-full cursor-pointer flex items-center justify-center transition-colors duration-200 shadow-sm" title={status.errors?.join(', ') || 'Has errors'}>
                                    <Typography variant="smallParagraph" color="white" fontWeight={700} className="text-xs leading-none">
                                        {status.errors?.length || '!'}
                                    </Typography>
                                </div>
                                {status.errors && status.errors.length > 0 && (
                                    <div className="absolute right-0 top-8 w-72 p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg text-xs z-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                                        <Typography variant="smallParagraph" color="#DC2626" fontWeight={600} className="mb-2">
                                            {status.errors.length} Error{status.errors.length > 1 ? 's' : ''} Found:
                                        </Typography>
                                        {status.errors.map((error, idx) => (
                                            <div key={idx} className="text-red-700 mb-1 leading-relaxed">• {error}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {status.status === SyncStatus.READY_TO_DEPLOY && (
                            <button 
                                onClick={() =>{
            // for all the plays and conditions... switch their requiested state to requested
            this.setState((state) => {
                if (!state.conditions || !state.plays) return null
                const correspondingConditionIdx = state.conditions.findIndex((v) => v.condition_id === correspondingConditionId || v.temp_condition_id === correspondingConditionId)
                if (correspondingConditionIdx === -1) return null
                const updatedPlays: Play[] = [...state.plays].map((v) => {
                    const includedPlay = (v.temp_condition_id && v.temp_condition_id === correspondingConditionId) || (v.condition_id && v.condition_id === correspondingConditionId)
                    return includedPlay && v.active ? {...v, 'deployRequested': true} : {...v}
                })

                return {
                    'conditions': [...state.conditions.slice(0, correspondingConditionIdx), {...state.conditions[correspondingConditionIdx], 'deployRequested': true, 'active': true}, ...state.conditions.slice(correspondingConditionIdx + 1)],
                    'plays': updatedPlays
                }
            })
        }}
                                className="px-3 py-1 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-md transition-colors duration-200"
                            >
                                Deploy
                            </button>
                        )}
                        {status.status === SyncStatus.DEPLOYING && (
                            <div className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-md">
                                Deploying...
                            </div>
                        )}
                        <button 
                            onClick={() => {
                                this.setState((state) => {
                                    if (!state.conditions || !state.plays || !correspondingConditionId) return null
                                    const correspondingConditionIdx = state.conditions.findIndex((v) => (v.condition_id !== _DEFAULT_ID && v.condition_id === correspondingConditionId) || (v.temp_condition_id && v.temp_condition_id === correspondingConditionId))
                                    if (correspondingConditionIdx === -1) return null
                                    const updatedPlays = [...state.plays].filter((x) => {
                                        if ((x.temp_condition_id && x.temp_condition_id === v.temp_condition_id) || (v.condition_id !== _DEFAULT_ID && v.condition_id === x.condition_id)) {
                                            if (x.play_id === _DEFAULT_ID && !x.deployRequested) return false
                                        } 
                                        return true
                                    }).map((x) => {
                                        if (x.temp_condition_id && x.temp_condition_id === v.temp_condition_id || (v.condition_id !== _DEFAULT_ID && v.condition_id === x.condition_id)) return {...x, 'active': false}
                                        return {...x}
                                    })

                                    let updatedConditions: Condition[] = []
                                    const correspondingCondition = state.conditions[correspondingConditionIdx]
                                    if (correspondingCondition.condition_id !== _DEFAULT_ID || correspondingCondition.deployRequested) {
                                        updatedConditions = [...state.conditions.slice(0, correspondingConditionIdx), {...state.conditions[correspondingConditionIdx], 'active': false}, ...state.conditions.slice(correspondingConditionIdx + 1)]
                                    } else {
                                        updatedConditions = [...state.conditions.slice(0, correspondingConditionIdx), ...state.conditions.slice(correspondingConditionIdx + 1)]
                                    }

                                    return {
                                        'conditions': [...state.conditions.slice(0, correspondingConditionIdx), {...state.conditions[correspondingConditionIdx], 'active': false}, ...state.conditions.slice(correspondingConditionIdx + 1)],
                                        'plays': updatedPlays,
                                    }
                                })
                            }}
                            className="flex items-center justify-center w-6 h-6 rounded-full bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 transition-colors duration-200"
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="text-red-600">
                                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            <div className="p-4 bg-gray-50">
                <PlayConditionComponent 
                totalPlays={(this.state.plays ?? []).length}
                totalPossibleMatches={this.state.totalPossibleMatches}
                onRemove={() => {}}
                onForceSave={() =>{}}
        syncedStatus={status.status}
        syncErrors={status.errors}
        updateCondition={(updatedCondition: Condition) => {
            // find matching condition
            this.setState((state) => {
                if (!state.conditions || !correspondingConditionId) return null
                // find the condition 
                const correspondingConditionIdx = state.conditions.findIndex((v) => v.condition_id === correspondingConditionId || v.temp_condition_id === correspondingConditionId)
                if (correspondingConditionIdx === -1) return null
                const currentCondition = state.conditions[correspondingConditionIdx]
                return {
                    'conditions': [
                        ...state.conditions.slice(0, correspondingConditionIdx), 
                        {...updatedCondition, 'condition_id': currentCondition.condition_id, 'temp_condition_id': currentCondition.temp_condition_id},
                        ...state.conditions.slice(correspondingConditionIdx + 1), 
                    ]
                }
            })
        }} 
        updatePlays={(updatedPlays: Play[]) => {
            this.setState((state) => {
                // find corresponding plays and update the values 
                if (!state.plays) return null
                const removedPlays = correspondingPlays.filter((v) => updatedPlays.find((x) => (x.temp_play_id && x.temp_play_id === v.temp_play_id) || (x.play_id !== _DEFAULT_ID && x.play_id === v.play_id)) === undefined)
                const newPlays = updatedPlays.filter((v) => correspondingPlays.find((x) => (x.temp_play_id && x.temp_play_id === v.temp_play_id) || (x.play_id !== _DEFAULT_ID && x.play_id === v.play_id)) === undefined)
                
                let updatedStatePlays = [...state.plays].filter((v) => {
                    return removedPlays.find((x) => (x.temp_play_id && x.temp_play_id === v.temp_play_id) || (x.play_id !== _DEFAULT_ID && x.play_id === v.play_id)) === undefined
                }).map((v) => {
                    const matchedPlay = updatedPlays.find((x) => (x.temp_play_id && x.temp_play_id === v.temp_play_id) || (x.play_id !== _DEFAULT_ID && x.play_id === v.play_id))
                    return matchedPlay ? {...matchedPlay} : v
                })
                updatedStatePlays = [...updatedStatePlays, ...newPlays]
                return {
                    'plays': updatedStatePlays
                }
            })
        }}
        condition={v} 
        plays={correspondingPlays} 
        groupIdToGroup={this.props.groupIdToGroup}/>
            </div>
        </div>
    }

    renderTabSelection() {
        return <div className="w-full flex flex-row justify-center items-center">
            <Filter 
            filterType={FilterType.SINGLE}
            variant={'largeParagraph'}
            selectableFilterProps={{
                'showAll': true,
                'removeOuterBoxForShowAll': true,
                'filterOptions': Object.values(TabOption).map((v) => {return {'label': v, 'selected': v === this.state.selectedTab, 'value': v}}),
                'onFilterUpdate': (updatedFilters: FilterOption[]) => {
                    const selected = updatedFilters.find((v) => v.selected)
                    if (!selected) return 
                    this.setState({'selectedTab': selected.value as TabOption})
                }
            }}
            />
        </div>
    }

    render() {
        if (!this.state.plays || !this.state.savedPlays || !this.state.conditions || !this.state.savedConditions) return <Loader />
        
        const activeConditions = this.state.conditions.filter((v) => this.state.selectedTab === TabOption.ACTIVE === v.active)
        
        // Show onboarding guide when no conditions exist
        if (activeConditions.length === 0) {
            return <div className="p-8 h-full min-w-full w-fit flex flex-col gap-6 items-center bg-gray-100 justify-center" style={{'userSelect': 'text'}}>
                <div className="max-w-5xl bg-white rounded-lg shadow-sm border border-gray-200 p-8">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="text-blue-600">
                                <path d="M12 2L2 7V10C2 16 6 20.5 12 22C18 20.5 22 16 22 10V7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </div>
                        <Typography variant="h1" color="#1F2937" fontWeight={700} className="mb-3">
                            Welcome to Scripts!
                        </Typography>
                        <Typography variant="largeParagraph" color="#6B7280" className="max-w-2xl mx-auto leading-relaxed">
                            Create intelligent triggers that provide your reps with contextual suggestions during live calls
                        </Typography>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                        <div className="text-center p-6 bg-blue-50 rounded-xl border border-blue-100">
                            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Typography variant="mediumParagraph" color="white" fontWeight={700} className="text-sm">1</Typography>
                            </div>
                            <Typography variant="h3" color="#1F2937" fontWeight={600} className="mb-3">
                                Set Up Conditions
                            </Typography>
                            <Typography variant="mediumParagraph" color="#6B7280" className="mb-4 leading-relaxed">
                                Define when suggestions should appear using smart filters:
                            </Typography>
                            <div className="text-left space-y-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                                    <Typography variant="smallParagraph" color="#4B5563">Time-based (e.g., "Before 40s")</Typography>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                                    <Typography variant="smallParagraph" color="#4B5563">Keywords spoken</Typography>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                                    <Typography variant="smallParagraph" color="#4B5563">Specific users/teams</Typography>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="w-2 h-2 bg-blue-400 rounded-full flex-shrink-0"></div>
                                    <Typography variant="smallParagraph" color="#4B5563">Prospect objections</Typography>
                                </div>
                            </div>
                        </div>

                        <div className="text-center p-6 bg-green-50 rounded-xl border border-green-100">
                            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                                <Typography variant="mediumParagraph" color="white" fontWeight={700} className="text-sm">2</Typography>
                            </div>
                            <Typography variant="h3" color="#1F2937" fontWeight={600} className="mb-3">
                                Create Suggestions
                            </Typography>
                            <Typography variant="mediumParagraph" color="#6B7280" className="mb-4 leading-relaxed">
                                Write the specific prompts your reps will see:
                            </Typography>
                            <div className="text-left bg-white rounded-lg p-4 border border-green-200 space-y-2">
                                <Typography variant="smallParagraph" color="#6B7280" className="italic mb-3 text-center">
                                    Example suggestions:
                                </Typography>
                                <Typography variant="smallParagraph" color="#374151" className="leading-relaxed">
                                    "Ask about their current process"
                                </Typography>
                                <Typography variant="smallParagraph" color="#374151" className="leading-relaxed">
                                    "Mention the ROI calculator"
                                </Typography>
                                <Typography variant="smallParagraph" color="#374151" className="leading-relaxed">
                                    "Schedule a demo"
                                </Typography>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-6 mb-8 border border-blue-100">
                        <Typography variant="h3" color="#1F2937" fontWeight={600} className="mb-6 text-center">
                            💡 Quick Start Tips
                        </Typography>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-white rounded-lg p-4 border border-green-200">
                                <Typography variant="mediumParagraph" color="#059669" fontWeight={600} className="mb-3">
                                    For Openers:
                                </Typography>
                                <Typography variant="smallParagraph" color="#374151" className="leading-relaxed">
                                    Set time condition "Before 40s" with suggestions like "Ask for permission to continue" or "Confirm their role"
                                </Typography>
                            </div>
                            <div className="bg-white rounded-lg p-4 border border-blue-200">
                                <Typography variant="mediumParagraph" color="#2563EB" fontWeight={600} className="mb-3">
                                    For Objections:
                                </Typography>
                                <Typography variant="smallParagraph" color="#374151" className="leading-relaxed">
                                    Use remark filters for "No budget" with responses like "Ask about their decision-making timeline"
                                </Typography>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <button
                            onClick={this._addNewOpener.bind(this)}
                            className="px-8 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 flex items-center gap-3 justify-center font-medium"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
                                <path d="M12 6L12 18M6 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Create Your First Opener
                        </button>
                        <button
                            onClick={this._addNewCondition.bind(this)}
                            className="px-8 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg shadow-sm transition-colors duration-200 flex items-center gap-3 justify-center font-medium"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-white">
                                <path d="M12 6L12 18M6 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Create Custom Play
                        </button>
                    </div>

                    <div className="mt-4 text-center">
                        <a
                            href="https://www.tella.tv/video/trellis-plays-scripts-walkthrough-6rqt"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-6 py-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors duration-200 font-medium"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="text-blue-600">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Watch Full Tutorial
                        </a>
                    </div>
                </div>
            </div>
        }
        
        return <div className="h-full flex flex-row bg-gray-100" style={{'userSelect': 'text'}}>
        <PromptOnLeave when={this._hasPending()} message={'You have unsaved changes. Sure you want to leave'}/>
        <PlaysSidebar
            conditions={activeConditions}
            plays={this.state.plays}
            groupIdToGroup={this.props.groupIdToGroup}
            visibleAccounts={this.props.visibleAccounts}
            onConditionClick={this._handleConditionClickFromSidebar.bind(this)}
            expandedConditions={this.state.expandedConditions}
        />
        <div className="flex-1 p-4 overflow-y-auto flex flex-col gap-4 items-center">
            {activeConditions.map((v) => this.renderCondition(v))}
            <div className="ml-auto bottom-0 p-2.5 sticky w-full flex flex-row gap-3 justify-center items-center text-center"
            style={{
                'pointerEvents': 'none',
                'zIndex': '999',
            }}
            >
                <button
                    onClick={this._addNewOpener.bind(this)}
                    className="pointer-events-auto px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 flex items-center gap-2"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-white">
                        <path d="M12 6L12 18M6 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    New Opener
                </button>
                <button
                    onClick={this._addNewCondition.bind(this)}
                    className="pointer-events-auto px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg shadow-sm transition-colors duration-200 flex items-center gap-2"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-white">
                        <path d="M12 6L12 18M6 12L18 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    New Play
                </button>
            </div>
        </div>
        </div>
    }
}


const ReduxWrapped = connect((state: RootState) => {
    return {
        groupIdToGroup: new Map(state.userGroupInfo.sortedGroupInfo.map(g => [g.group.user_group_id, g.group])),
        groupsHasLoaded: state.userGroupInfo.hasLoaded,
        visibleAccounts: convertFromReduxSafeVisibleAccounts(state.visibleAccounts, (u) => u.can_dial && u.team_is_active)
    }
})(Plays)

const PlaysWithAdminAccess = () => (
    <RequiresAdminAccess>
        <ReduxWrapped />
    </RequiresAdminAccess>
)

export { PlaysWithAdminAccess as Plays}